import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Home.css';
import { breakingHeadlineAPI } from '../utils/api/breakingHeadlineAPI';
import { videosAPI } from '../utils/api/adminAPI';
import { API_ORIGIN } from '../utils/api/config';
import homeNewsAPI from '../utils/api/homeNewsAPI';

const IMAGE_URLS = [
  'https://images.loksattaimg.com/2026/03/New-Project-2026-03-13T162455.659.jpg?w=440',
  'https://images.bhaskarassets.com/web2images/521/2026/03/13/61b1326c-19ba-4fe4-af0a-daadfa9e8130_1773375079455.jpg',
  'https://img.etimg.com/thumb/width-420,height-315,imgsize-46610,resizemode-75,msid-129551415/news/defence/iran-is-not-another-venezuela-trump-has-found-to-great-cost.jpg',
  'https://img.mathrubhumi.com/view/acePublic/alias/contentid/1prpk90wxdwuz5rcml9/0/trisha-jpg.webp?f=16%3A9&q=0.75&w=532',
  'https://www.newsonair.gov.in/wp-content/uploads/2025/09/yogi.jpg',
  'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSkLxOl7IMykBJYvH40VacWM1b9ubR0NX2ivA&s'
];

const fallbackHeadlines = [
  'चित्रकूट में नई सड़क परियोजना शुरू हुई - 50 किमी लंबी सड़क का निर्माण',
  'राजनीति: नई नीति की घोषणा केंद्र सरकार द्वारा',
  'शिक्षा: 100 नए स्कूल खुलने वाले हैं इस साल',
  'मनोरंजन: बॉलीवुड की नई फिल्म रिलीज हुई',
  'स्वास्थ्य: नई स्वास्थ्य सेवाएं ग्रामीण इलाकों तक पहुंचीं'
];

const SECTION_ORDER = [
  'सबसे नई खबरें',
  'राजनीति',
  'शिक्षा',
  'धर्म',
  'मनोरंजन',
  'स्वास्थ्य',
  'आपके लिए',
  'और भी कहानियाँ'
];

const CATEGORY_SECTIONS = ['राजनीति', 'शिक्षा', 'धर्म', 'मनोरंजन', 'स्वास्थ्य'];

const getFallbackImage = (key = 0) => IMAGE_URLS[key % IMAGE_URLS.length];

const resolveNewsImage = (value, index = 0) => {
  if (!value) {
    return getFallbackImage(index);
  }

  const imageUrl = String(value).trim();

  if (!imageUrl) {
    return getFallbackImage(index);
  }

  if (/^https?:\/\/localhost(?::\d+)?\/uploads\//i.test(imageUrl)) {
    try {
      const parsedUrl = new URL(imageUrl);
      return `${API_ORIGIN}${parsedUrl.pathname}`;
    } catch {
      return getFallbackImage(index);
    }
  }

  if (/^https?:\/\//i.test(imageUrl) || imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  if (imageUrl.startsWith('/uploads/')) {
    return `${API_ORIGIN}${imageUrl}`;
  }

  if (imageUrl.startsWith('uploads/')) {
    return `${API_ORIGIN}/${imageUrl}`;
  }

  return imageUrl;
};

const formatDate = (value) => {
  if (!value) {
    return 'आज';
  }

  try {
    return new Intl.DateTimeFormat('hi-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(value));
  } catch (error) {
    return 'आज';
  }
};

const normalizeNewsItem = (item, index = 0) => ({
  id: item?._id || item?.id || `fallback-${index}`,
  slug: item?.slug || '',
  headline: item?.title || 'समाचार उपलब्ध नहीं है',
  description: item?.description || '',
  content: item?.content || '',
  image: resolveNewsImage(item?.image, index),
  category: item?.category || 'होम न्यूज़',
  date: formatDate(item?.createdAt),
  views: Number(item?.views || 0),
  author: item?.author || 'Admin',
  location: item?.location || '',
  type: item?.videoUrl ? 'video' : 'article'
});

const buildHomeNewsPath = (news) => (news?.slug ? `/home-news/${news.slug}` : '/');

const HeroSection = ({ news }) => (
  <div className="home-hero-section">
    <div className="home-hero-card">
      <Link to={buildHomeNewsPath(news)} className="home-hero-media-link">
        <img
          src={news.image}
          alt={news.headline}
          className="home-hero-media"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = getFallbackImage(0);
          }}
        />
      </Link>
      <div className="home-hero-content">
        <Link to={buildHomeNewsPath(news)} className="home-hero-title-link">
          <h1 className="home-hero-title">{news.headline}</h1>
        </Link>
        <p className="home-hero-summary">{news.description}</p>
        <div className="home-hero-meta">
          <span className="home-hero-tag">{news.category}</span>
          <span className="home-hero-detail">{news.date}</span>
          {news.location ? <span className="home-hero-detail">{news.location}</span> : null}
        </div>
      </div>
    </div>
  </div>
);

const BreakingTicker = ({ headlines }) => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const tickerStep = 0.09;
    const tickerInterval = 110;

    const interval = setInterval(() => {
      setScrollPosition((prev) => (prev + tickerStep) % 100);
    }, tickerInterval);

    return () => clearInterval(interval);
  }, []);

  const displayHeadlines = headlines && headlines.length > 0
    ? headlines.map((headline) => headline.headline)
    : fallbackHeadlines;

  return (
    <div className="ticker-wrapper">
      <div className="ticker-label">
        <span className="ticker-icon">📢</span> LATEST
      </div>
      <div className="ticker-container">
        <div className="ticker-content" style={{ transform: `translateX(-${scrollPosition}%)` }}>
          {[...displayHeadlines, ...displayHeadlines].map((headline, idx) => (
            <span key={`${headline}-${idx}`} className="ticker-item">
              {headline}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const NewsCard = ({ news }) => (
  <Link to={buildHomeNewsPath(news)} className="news-card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
    <div className="card-image-wrapper">
      <img
        src={news.image}
        alt={news.headline}
        className="card-image"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = getFallbackImage(5);
        }}
      />
      {news.type === 'video' && <div className="video-badge">▶ VIDEO</div>}
      <div className="category-tag">{news.category}</div>
    </div>
    <div className="card-content">
      <h3 className="card-headline">{news.headline}</h3>
      {news.description ? <p className="card-description">{news.description}</p> : null}
      <div className="card-footer">
        <span className="card-date">{news.date}</span>
        <span className="card-views">👁 {(news.views / 1000).toFixed(1)}K</span>
      </div>
    </div>
  </Link>
);

const TrendingSidebar = ({ news }) => {
  const trendingNews = news.slice(0, 5);

  return (
    <div className="trending-sidebar">
      <h2 className="sidebar-title">🔥 TRENDING</h2>
      <div className="trending-list">
        {trendingNews.map((item, idx) => (
          <Link
            key={item.id}
            to={buildHomeNewsPath(item)}
            className="trending-item"
            style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
          >
            <div className="trending-rank">{idx + 1}</div>
            <div className="trending-content">
              <h4 className="trending-headline">{item.headline}</h4>
              <div className="trending-meta">
                <span>{item.category}</span>
                <span>👁 {(item.views / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const SidebarLatestVideos = ({ news }) => {
  const latestVideos = news.filter((item) => item.type === 'video').slice(0, 4);

  return (
    <div className="sidebar-card latest-videos-card">
      <h3 className="sidebar-card-title">Latest Videos</h3>
      <div className="sidebar-video-list">
        {latestVideos.map((item) => (
          <Link
            key={item.id}
            to={buildHomeNewsPath(item)}
            className="sidebar-video-item"
            style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
          >
            <img
              src={item.image}
              alt={item.headline}
              className="sidebar-video-thumb"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = getFallbackImage(4);
              }}
            />
            <div className="sidebar-video-content">
              <p className="sidebar-video-title">{item.headline}</p>
              <span className="sidebar-video-meta">{item.date}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const QuickLinksCard = () => {
  const quickLinks = [
    { label: 'ब्रेकिंग न्यूज़', to: '/breaking-news' },
    { label: 'उत्तर प्रदेश', to: '/up-news' },
    { label: 'राजनीति', to: '/politics' },
    { label: 'वीडियो', to: '/videos' },
    { label: 'संपर्क', to: '/contact' }
  ];

  return (
    <div className="sidebar-card quick-links-card">
      <h3 className="sidebar-card-title">🔗 Quick Links</h3>
      <div className="quick-links-list">
        {quickLinks.map((link) => (
          <Link key={link.to} to={link.to} className="quick-link-item">
            <span>{link.label}</span>
            <span>→</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const PopularCategoriesCard = ({ news }) => {
  const categoryStats = Object.entries(
    news.reduce((accumulator, item) => {
      accumulator[item.category] = (accumulator[item.category] || 0) + 1;
      return accumulator;
    }, {})
  )
    .sort((first, second) => second[1] - first[1])
    .slice(0, 5);

  return (
    <div className="sidebar-card popular-categories-card">
      <h3 className="sidebar-card-title">Popular Categories</h3>
      <div className="popular-category-list">
        {categoryStats.map(([category, count], index) => (
          <div key={category} className="popular-category-item">
            <span className="popular-category-rank">{index + 1}</span>
            <span className="popular-category-name">{category}</span>
            <span className="popular-category-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdPromoCard = () => (
  <div className="featured-box ad-promo-box">
    <div className="featured-label">PROMOTION</div>
    <img src={getFallbackImage(3)} alt="Featured" className="featured-image" />
    <div className="ad-promo-content">
      <h3>विशेष योजना</h3>
      <p>नई सरकारी योजना, स्थानीय अपडेट और जरूरी घोषणाएं एक ही जगह देखें।</p>
      <Link to="/about" className="ad-promo-btn">
        और जानें
      </Link>
    </div>
  </div>
);

const YouTubePlayer = () => {
  const [latestVideo, setLatestVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(true);

  useEffect(() => {
    const fetchLatestVideo = async () => {
      try {
        setLoadingVideo(true);
        const response = await videosAPI.getAllVideos(1, 1);
        if (response.success && response.videos && response.videos.length > 0) {
          setLatestVideo(response.videos[0]);
        }
      } catch (error) {
        console.error('Error fetching latest video:', error);
      } finally {
        setLoadingVideo(false);
      }
    };

    fetchLatestVideo();
  }, []);

  const getYouTubeEmbedUrl = (youtubeLink) => {
    if (!youtubeLink) return '';
    if (youtubeLink.includes('youtube.com/embed/')) return youtubeLink;

    let videoId = null;

    if (youtubeLink.includes('youtube.com/watch?v=')) {
      videoId = youtubeLink.split('v=')[1].split('&')[0];
    } else if (youtubeLink.includes('youtu.be/')) {
      videoId = youtubeLink.split('youtu.be/')[1].split('?')[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : youtubeLink;
  };

  return (
    <section className="youtube-section">
      <div className="youtube-header">
        <h2 className="youtube-title">Featured Video</h2>
        <p className="youtube-subtitle">देखें हमारा लेटेस्ट वीडियो</p>
      </div>
      <div className="youtube-container">
        <div className="youtube-wrapper">
          {loadingVideo ? (
            <div className="loading-placeholder">
              <p>वीडियो लोड हो रहा है...</p>
            </div>
          ) : latestVideo ? (
            <>
              <iframe
                width="100%"
                height="500"
                src={getYouTubeEmbedUrl(latestVideo.youtubeLink)}
                title={latestVideo.title || 'Featured Video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-same-origin allow-scripts allow-popups allow-presentation"
                className="youtube-iframe"
              ></iframe>
              <div className="video-info" style={{ marginTop: '12px', padding: '0 16px' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#333' }}>{latestVideo.title}</h3>
                <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                  {latestVideo.description?.substring(0, 100)}...
                </p>
              </div>
            </>
          ) : (
            <div className="loading-placeholder">
              <p>कोई वीडियो उपलब्ध नहीं</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const CategorySection = ({ title, items }) => (
  <section className="category-section">
    <div className="section-header">
      <h2 className="section-title">{title}</h2>
      <button className="view-all" onClick={() => window.scrollTo(0, 0)}>सब देखें →</button>
    </div>
    <div className="news-grid">
      {items.map((item, index) => (
        <NewsCard key={item.id || `${title}-${index}`} news={item} />
      ))}
    </div>
  </section>
);

export default function Home() {
  const [sections, setSections] = useState({});
  const [featuredNews, setFeaturedNews] = useState(normalizeNewsItem(null));
  const [trendingNews, setTrendingNews] = useState([]);
  const [allNews, setAllNews] = useState([]);
  const [breakingHeadlines, setBreakingHeadlines] = useState([]);
  const [loadingHeadlines, setLoadingHeadlines] = useState(true);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const response = await homeNewsAPI.getHomepageNews(12);
        if (!response.success || !response.data) {
          return;
        }

        const nextSections = {};
        SECTION_ORDER.forEach((sectionName) => {
          nextSections[sectionName] = (response.data.sections?.[sectionName] || []).map((item, index) =>
            normalizeNewsItem(item, index)
          );
        });

        const combinedItems = SECTION_ORDER.flatMap((sectionName) => nextSections[sectionName] || []);
        setSections(nextSections);
        setAllNews(combinedItems);
        setFeaturedNews(
          response.data.featured ? normalizeNewsItem(response.data.featured, 0) : combinedItems[0] || normalizeNewsItem(null)
        );
        setTrendingNews((response.data.trending || []).map((item, index) => normalizeNewsItem(item, index)));
      } catch (error) {
        console.error('Failed to fetch homepage home news:', error);
      }
    };

    const fetchHeadlines = async () => {
      try {
        const data = await breakingHeadlineAPI.getBreakingHeadlines();
        setBreakingHeadlines(data && Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch breaking headlines:', error);
        setBreakingHeadlines([]);
      } finally {
        setLoadingHeadlines(false);
      }
    };

    fetchHomepageData();
    fetchHeadlines();
  }, []);

  const latestNews = sections['सबसे नई खबरें'] || [];
  const forYouNews = sections['आपके लिए'] || [];
  const moreStories = sections['और भी कहानियाँ'] || [];
  const sidebarNews = trendingNews.length > 0 ? trendingNews : allNews;

  return (
    <div className="home-container">
      {!loadingHeadlines && <BreakingTicker headlines={breakingHeadlines} />}

      <div className="home-content">
        <HeroSection news={featuredNews} />

        <YouTubePlayer />

        <div className="main-grid">
          <div className="main-content">
            <section className="latest-news-section">
              <div className="section-header">
                <h2 className="section-title">सबसे नई खबरें</h2>
              </div>
              <div className="news-grid large-grid">
                {latestNews.map((news, index) => (
                  <NewsCard key={news.id || `latest-${index}`} news={news} />
                ))}
              </div>
            </section>

            {CATEGORY_SECTIONS.map((category) => (
              <CategorySection key={category} title={category} items={sections[category] || []} />
            ))}

            <section className="more-news-section">
              <div className="section-header">
                <h2 className="section-title">आपके लिए</h2>
              </div>
              <div className="news-grid">
                {forYouNews.map((news, index) => (
                  <NewsCard key={news.id || `for-you-${index}`} news={news} />
                ))}
              </div>
            </section>
          </div>

          <aside className="sidebar">
            <TrendingSidebar news={sidebarNews} />
            <SidebarLatestVideos news={allNews} />
            <QuickLinksCard />
            <PopularCategoriesCard news={allNews} />
            <AdPromoCard />
          </aside>
        </div>

        <section className="stories-section">
          <div className="section-header full-width">
            <h2 className="section-title">और भी कहानियाँ</h2>
          </div>
          <div className="stories-grid">
            {moreStories.map((news, index) => (
              <NewsCard key={news.id || `story-${index}`} news={news} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}